import React from 'react';

import { LayoutGrid, List, Map as MapIcon } from 'lucide-react';

export type ExploreView = 'map' | 'list';

interface ExploreViewToggleProps {
  view: ExploreView;
  onChange: (view: ExploreView) => void;
  /**
   * Drawn inside another control (the search box) rather than standing on its own. Drops the
   * pill's own border, fill and shadow, and fits the 48px search box with a little room around it.
   */
  embedded?: boolean;
}

interface ViewOption {
  value: ExploreView;
  label: string;
  icon: React.ReactNode;
}

// Two views: the map, and the courses as a list. The client cut the separate Card view. What the
// list looks like now depends on the screen, not on a button: a phone gets dense rows and a wider
// screen gets the card grid (CourseListView decides, at the strip's sm breakpoint). So the second
// segment shows the matching icon: rows below sm, a grid from sm up.
// Map first, because it is what Explore opens on.
const OPTIONS: ViewOption[] = [
  {
    value: 'map',
    label: 'Map view',
    icon: <MapIcon className="h-5 w-5" />,
  },
  {
    value: 'list',
    label: 'List view',
    icon: (
      <>
        <List className="h-5 w-5 sm:hidden" />
        <LayoutGrid className="hidden h-5 w-5 sm:block" />
      </>
    ),
  },
];

// Deliberately not the shared `ViewToggle` / `useViewMode` in components/common. That pair is built
// for the admin CRUD screens: it namespaces its stored preference by the user's *role* (Explore is
// public, so there is no role), and it toggles on any `t` keypress anywhere on the window — which
// would fire while the visitor is typing into the search box right next to it.
//
// A segmented control, not a single round flip-button. A flip-button shows the icon of the view it
// would switch *to*, so it never says where you are. Marked segments answer that without a tap.
const ExploreViewToggle: React.FC<ExploreViewToggleProps> = ({
  view,
  onChange,
  embedded = false,
}) => (
  <div
    role="group"
    aria-label="Choose how to see the courses"
    // One pill holding the segments, the same charcoal-and-gold shell as the 48px round buttons
    // beside it, so the whole row still reads as one set of map controls. h-12 matches them
    // exactly; the segments divide that height rather than adding to it.
    className={
      embedded
        ? 'flex h-10 shrink-0 items-center gap-0.5 rounded-full bg-white/5 p-0.5'
        : 'flex h-12 shrink-0 items-center gap-1 rounded-full border border-primary/30 bg-charcoal/90 p-1 shadow-2xl backdrop-blur'
    }
  >
    {OPTIONS.map(({ value, label, icon }) => {
      const isActive = view === value;

      return (
        <button
          key={value}
          type="button"
          onClick={() => onChange(value)}
          aria-label={label}
          aria-pressed={isActive}
          title={label}
          className={`flex ${embedded ? 'h-9 w-9' : 'h-10 w-10'} items-center justify-center rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary ${
            isActive
              ? // BRAND.md: text on a gold fill is BLACK. White on gold and gold on gold are both
                // 2.32:1 and neither is allowed.
                'bg-primary text-secondary'
              : 'text-primary hover:bg-white/10'
          }`}
        >
          {icon}
        </button>
      );
    })}
  </div>
);

export default ExploreViewToggle;
